import { useState, useEffect, type ComponentProps } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ExamTable } from './ExamTable';
import { ExamStatusFilter } from './ExamStatusFilter';
import { ExamDialog } from './ExamDialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { examSchema, type ExamFormData, defaultExamValues } from './utils/examValidation';
import { getExaminerOrganizations } from '@/lib/firebase/queries/organizations/getUserOrganizations';
import { getOrganizations } from '@/lib/firebase/queries/organizations/getOrganizations';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { Organization } from '@/types/organization';
import { RemindQuestionnaireButton } from '@/components/health/RemindQuestionnaireButton';
import { CopyQuestionnaireLinkButton } from '@/components/health/CopyQuestionnaireLinkButton';

/** `allOrganizations`: doctors and admins pick from every organization (#93); examiners only from theirs. */
export function CustomerExamList({ allOrganizations = false }: { allOrganizations?: boolean } = {}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedExamStatus, setSelectedExamStatus] = useState<string | null>(null);
  const [selectedQuestionnaireStatus, setSelectedQuestionnaireStatus] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<ComponentProps<typeof ExamDialog>['customer'] | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  // #97: who in the current list has not filled the questionnaire (not valid).
  const [notFilledIds, setNotFilledIds] = useState<string[]>([]);
  const { t, dir } = useLanguage();
  const { currentUser } = useAuth();

  const form = useForm<ExamFormData>({
    resolver: zodResolver(examSchema),
    defaultValues: defaultExamValues
  });

  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!currentUser?.uid) return;
      
      try {
        // Examiners: the organizations they are assigned to; doctors and admins: all (#93)
        const orgs = allOrganizations ? await getOrganizations() : await getExaminerOrganizations(currentUser.uid);
        setOrganizations(orgs);
        
        // If examiner has only one organization, auto-select it
        if (orgs.length === 1) {
          setSelectedOrganization(orgs[0].id);
        }
      } catch (error) {
        console.error('Error fetching examiner organizations:', error);
      }
    };
    fetchOrganizations();
  }, [currentUser?.uid, allOrganizations]);

  useEffect(() => {
    setNotFilledIds([]);
  }, [selectedOrganization]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Save logic here
      setSelectedCustomer(null);
    } catch (error) {
      console.error('Error saving exam:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleComplete = async () => {
    setIsSaving(true);
    try {
      // Complete logic here
      setSelectedCustomer(null);
    } catch (error) {
      console.error('Error completing exam:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6" dir={dir}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className={`absolute ${dir === 'rtl' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground`} />
            <Input
              placeholder={t('dashboard.examiner.customers.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={dir === 'rtl' ? 'pr-9' : 'pl-9'}
            />
          </div>
          <Select
            value={selectedOrganization}
            onValueChange={setSelectedOrganization}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder={t('dashboard.examiner.organizations.select')} />
            </SelectTrigger>
            <SelectContent>
              {organizations.map((org) => (
                <SelectItem key={org.id} value={org.id}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <CopyQuestionnaireLinkButton />
        </div>
        
        <ExamStatusFilter 
          selectedExamStatus={selectedExamStatus}
          selectedQuestionnaireStatus={selectedQuestionnaireStatus}
          onExamStatusChange={setSelectedExamStatus}
          onQuestionnaireStatusChange={setSelectedQuestionnaireStatus}
          questionnaireNotCompletedCount={selectedOrganization ? notFilledIds.length : undefined}
        />
        {selectedOrganization && selectedQuestionnaireStatus === 'questionnaire-not-completed' && (
          <div>
            <RemindQuestionnaireButton organizationId={selectedOrganization} userIds={notFilledIds} />
          </div>
        )}
      </div>

      {!selectedOrganization && organizations.length > 1 ? (
        <div className="text-center py-8 text-muted-foreground">
          {t('dashboard.examiner.organizations.selectToView')}
        </div>
      ) : (
        <ExamTable 
          searchQuery={searchQuery}
          examStatusFilter={selectedExamStatus}
          questionnaireStatusFilter={selectedQuestionnaireStatus}
          organizationId={selectedOrganization}
          onNotFilledChange={setNotFilledIds}
          onExamClick={(c) =>
            setSelectedCustomer({ id: c.id, firstNameEn: c.firstNameEn, lastNameEn: c.lastNameEn, email: c.email, lastExam: c.lastExam ?? undefined })
          }
        />
      )}

      <ExamDialog
        customer={selectedCustomer}
        open={!!selectedCustomer}
        onOpenChange={(open) => !open && setSelectedCustomer(null)}
        form={form}
        onSave={handleSave}
        onComplete={handleComplete}
        isSaving={isSaving}
      />
    </div>
  );
}